import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Users, MapPin, FileText, Landmark, Loader2, FileSignature } from "lucide-react";
import { PageLoading, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { Checklist } from "./Checklist";
import { useReadOnly } from "./common";
import { startContract } from "./Contracts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SubCompanyDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const readOnly = useReadOnly();
  const [rk, setRk] = useState(0);
  const setEst = async (v) => { await api.put(`/subcon/companies/${id}/establishment`, { establishment: v }); setD({ ...d, establishment: v }); setRk(rk + 1); };
  const [d, setD] = useState(null);
  const [fa, setFa] = useState(null);
  const [faBusy, setFaBusy] = useState(false);
  useEffect(() => { api.get(`/subcon/companies/${id}`).then((r) => setD(r.data.company)).catch(() => setD(false)); }, [id]);
  const resolveFa = async () => { setFaBusy(true); try { const { data } = await api.get(`/subcon/companies/${id}/finanzamt`); setFa(data.finanzamt || { none: true }); if (!data.finanzamt) toast.warning(t("sub.faNone")); } catch { toast.error("Error"); } finally { setFaBusy(false); } };
  if (d === null) return <PageLoading />;
  if (d === false) return <div className="text-sm text-slate-500">—</div>;
  const rows = [
    [t("sub.fCountry"), d.country], [t("sub.fRegNo"), d.company_registration_number], [t("sub.fVat"), d.vat_id],
    [t("sub.fTaxNo"), d.tax_number], [t("sub.fAddress"), [d.street, d.postal_code, d.city].filter(Boolean).join(", ")],
    [t("sub.fContact"), d.contact_person], [t("sub.fEmail"), d.email], [t("sub.fPhone"), d.phone], [t("sub.fWebsite"), d.website],
  ];
  return (
    <div data-testid="sub-company-detail">
      <button onClick={() => nav("/app/employer/subcontractors/companies")} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="company-back"><ArrowLeft className="h-4 w-4" /> {t("sub.tCompanies")}</button>
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-white"><Building2 className="h-6 w-6" /></span>
        <div><h2 className="font-heading text-2xl font-extrabold text-navy">{d.trading_name || d.legal_name}</h2>{d.trading_name && <p className="text-sm text-slate-400">{d.legal_name}</p>}</div>
        {!readOnly && <Button onClick={() => startContract(nav, id).catch(() => toast.error("Error"))} data-testid="company-create-contract" className="ml-auto rounded-full bg-cyan text-white hover:bg-cyan-dark"><FileSignature className="mr-1 h-4 w-4" /> {t("sc.createFor")}</Button>}
      </div>
      <div className="mb-6 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-slate-500">{t("sc.establishment")}</span>
        <Select value={d.establishment || ""} onValueChange={setEst} disabled={readOnly}>
          <SelectTrigger className="w-48" data-testid="company-establishment"><SelectValue placeholder="Auto" /></SelectTrigger>
          <SelectContent>{["DE", "EU_EEA", "CH", "THIRD"].map((v) => <SelectItem key={v} value={v}>{t(`sc.est.${v}`)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card-soft p-6">
          <h3 className="mb-4 flex items-center gap-2 font-heading font-bold text-navy"><FileText className="h-4 w-4 text-cyan-dark" /> {t("sub.detCompany")}</h3>
          <dl className="space-y-2.5 text-sm">
            {rows.map(([k, v], i) => (<div key={i} className="flex justify-between gap-4"><dt className="text-slate-400">{k}</dt><dd className="text-right font-medium text-navy">{v || "—"}</dd></div>))}
          </dl>
        </div>
        <div className="card-soft p-6">
          <h3 className="mb-4 flex items-center gap-2 font-heading font-bold text-navy"><Users className="h-4 w-4 text-cyan-dark" /> {t("sub.tWorkers")} <span className="text-slate-400">({d.worker_count})</span></h3>
          {(!d.workers || d.workers.length === 0) ? <p className="text-sm text-slate-400">{t("sub.wkEmpty")}</p> : (
            <ul className="divide-y divide-slate-100" data-testid="company-workers-list">
              {d.workers.map((w) => (
                <li key={w.id} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="font-medium text-navy">{w.first_name} {w.last_name}</span>
                  <span className="text-slate-400">{w.occupation || w.nationality || "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="card-soft mt-6 p-6" data-testid="finanzamt-card">
        <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-navy"><Landmark className="h-4 w-4 text-cyan-dark" /> {t("sub.faTitle")}</h3>
        {fa && !fa.none ? (
          <div className="space-y-1.5 text-sm">
            <div className="text-lg font-bold text-navy" data-testid="finanzamt-name">{fa.finanzamt_name}</div>
            <p className="text-slate-500">{fa.reason}</p>
            {fa.source_url ? <a href={fa.source_url} target="_blank" rel="noreferrer" className="text-cyan-dark underline">{t("tax.viewSource")}</a> : (fa.legal_source && <p className="text-xs text-slate-400">{fa.legal_source}</p>)}
            <p className="text-xs text-slate-400">{t("sub.faVerified")}: {fmtDate(fa.last_verified_at) || "—"}</p>
            <p className="mt-2 text-[11px] italic text-slate-400">{t("sub.faDisclaimer")}</p>
          </div>
        ) : fa && fa.none ? <p className="text-sm text-slate-500">{t("sub.faNone")}</p> : (
          <Button onClick={resolveFa} disabled={faBusy} data-testid="resolve-finanzamt-btn" variant="outline" className="rounded-full">{faBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sub.faResolve")}</Button>
        )}
      </div>
      <div className="mt-6"><Checklist sid={id} readOnly={readOnly} refreshKey={rk} /></div>
    </div>
  );
}
